import {t} from '@lingui/macro'
import queryString from 'qs'
import React from 'react'
import useSWR from 'swr'
import useSWRImmutable from 'swr/immutable'

import type {Amount, BankAccountInfo, CurrencyType, GetWalletBalanceResponse} from '@pleo-io/deimos'

import config from 'config'
import {reportError} from '~/error/report'
import type {
    AutoTopUpSettingsParams,
    ManageAutoTopUpParams,
} from '~/feature-funds-management/tracking-manage-auto-top-up'
import {
    ManageAutoTopUpAction,
    trackManageAutoTopUp,
} from '~/feature-funds-management/tracking-manage-auto-top-up'
import {useToaster} from '~/shell/toaster'
import {useLoggedInUser, useUser} from '~/shell/user'

import {getDeimos} from './helpers'

import {callApi, request} from '../api'
import type {
    AutoTopUpSettings,
    CompanyTopUpSettings,
    OpenBankingAccountIdentificationType,
    WalletSettingsFeature,
} from '../types/wallet'

const baseUrl = config.endpoints.api

export type WalletBalanceSchema = Omit<GetWalletBalanceResponse, 'currency'> & {
    currency: CurrencyType
}

export function useWalletBalance(bypassHook = false) {
    const user = useLoggedInUser()
    const result = useSWR<WalletBalanceSchema, Error>(
        user?.companyId && !bypassHook
            ? `rest/v1/companies/${user?.companyId}/balance/current-available`
            : null,
        getDeimos,
    )
    return {
        ...result,
        isLoading: !result.data && !result.error,
    }
}

export interface CompletedWalletLoad {
    amount: Amount
    bill: Amount
    performed: string
    type: 'LOAD' | 'UNLOAD'
    status: 'COMPLETED'
}

export function useCompletedWalletLoads(companyId?: string) {
    const result = useSWR<CompletedWalletLoad[], Error>(
        companyId ? `/rest/v1/companies/${companyId}/loads` : null,
        getDeimos,
        {revalidateIfStale: false},
    )

    return {
        ...result,
        isLoading: companyId && !result.data && !result.error,
    }
}

async function updateCompanyTopUpSettings({
    companyId,
    payload,
}: {
    companyId: string
    payload: Partial<CompanyTopUpSettings>
}): Promise<CompanyTopUpSettings> {
    return request(`${baseUrl}/wallet-topup/rest/v1/companies/${companyId}/settings`, {
        auth: 'user',
        method: 'PUT',
        body: payload,
    })
}

function trackTopUpSettingsUpdate(
    companyTopUpSettings: Partial<CompanyTopUpSettings>,
    prevTopUpSettings?: CompanyTopUpSettings,
) {
    if (companyTopUpSettings.autoTopupType && companyTopUpSettings.autoTopupStatus) {
        const commonTrackingParams: AutoTopUpSettingsParams = {
            atu_type: companyTopUpSettings.autoTopupType,
            atu_status: companyTopUpSettings.autoTopupStatus,
            atu_amount_low_balance: companyTopUpSettings.lowBalanceTopup?.amount,
            atu_threshold_low_balance: companyTopUpSettings.lowBalanceTopup?.threshold,
            atu_amount_scheduled: companyTopUpSettings.scheduledTopup?.amount,
            atu_frequency_scheduled: companyTopUpSettings.scheduledTopup?.frequency,
        }

        let analyticsParams: ManageAutoTopUpParams
        if (
            !prevTopUpSettings?.autoTopupType ||
            (prevTopUpSettings?.autoTopupStatus !== 'ENABLED' &&
                prevTopUpSettings?.autoTopupStatus !== 'PENDING')
        ) {
            // if prev ATU is not active
            analyticsParams = {
                action: ManageAutoTopUpAction.ACTIVATE,
                ...commonTrackingParams,
            }
        } else if (
            companyTopUpSettings.autoTopupStatus === 'DISABLED' ||
            companyTopUpSettings.autoTopupStatus === 'DISABLED_BY_USER'
        ) {
            // if user wants to disable ATU
            analyticsParams = {
                action: ManageAutoTopUpAction.DISABLE,
                ...commonTrackingParams,
            }
        } else if (prevTopUpSettings.autoTopupType === companyTopUpSettings.autoTopupType) {
            // if something was changed, but prev ATU type is the same
            analyticsParams = {
                action: ManageAutoTopUpAction.CHANGE_SETTINGS,
                ...commonTrackingParams,
            }
        } else {
            // if prev ATU type is NOT the same
            analyticsParams = {
                action: ManageAutoTopUpAction.SWITCH_STRATEGY,
                prev_auto_top_up_type: prevTopUpSettings.autoTopupType,
                ...commonTrackingParams,
            }
        }

        trackManageAutoTopUp(analyticsParams)
    }
}

export function useCompanyTopUpSettings() {
    const {showToast} = useToaster()
    const user = useUser()
    const companyId = user?.companyId

    const result = useSWR<CompanyTopUpSettings>(
        companyId ? `/wallet-topup/rest/v1/companies/${companyId}/settings` : null,
        getDeimos,
        {revalidateIfStale: false},
    )

    const isLoading = !result.data && !result.error

    async function updateTopUpSettings(companyTopUpSettings: Partial<CompanyTopUpSettings>) {
        if (!companyId || !result) {
            showToast(
                t`We could not save the details of your Auto Top-Up settings correctly. Please reach out to support.`,
                {level: 'error'},
            )
            return
        }

        trackTopUpSettingsUpdate(companyTopUpSettings, result.data)

        const payload: CompanyTopUpSettings = {
            ...result.data,
            ...companyTopUpSettings,
            // this line is necessary to avoid TS error
            autoTopupStatus:
                companyTopUpSettings.autoTopupStatus ??
                result.data?.autoTopupStatus ??
                'DISABLED_BY_USER',
        }

        await result.mutate(updateCompanyTopUpSettings({companyId, payload}), false)
    }

    async function disableAutoTopUp() {
        if (!companyId || !result) {
            return
        }

        const payload: CompanyTopUpSettings = {
            ...result.data,
            autoTopupStatus: 'DISABLED_BY_USER',
        }

        await updateTopUpSettings(payload)
    }

    return {
        ...result,
        isLoading,
        mutations: {updateTopUpSettings, disableAutoTopUp},
    }
}

export function useAutoTopUpSettings() {
    const {data: companyTopUpSettings, isLoading} = useCompanyTopUpSettings()
    const {
        autoTopupStatus = 'DISABLED_BY_USER',
        autoTopupType,
        paymentRail,
        lowBalanceTopup,
        scheduledTopup,
    } = companyTopUpSettings ?? {}

    const autoTopUp: AutoTopUpSettings = {
        autoTopupStatus,
        autoTopupType,
        paymentRail,
        lowBalanceTopup,
        scheduledTopup,
    }

    return {
        autoTopUp,
        isLoading,
    }
}

export function useAutoTopUpEnabled() {
    const {autoTopUp} = useAutoTopUpSettings()
    const {autoTopupStatus, autoTopupType} = autoTopUp ?? {}
    const isAutoTopUpEnabled = autoTopupStatus === 'ENABLED' && autoTopupType === 'LOW_BALANCE'
    const isScheduledTopUpEnabled = autoTopupStatus === 'ENABLED' && autoTopupType === 'SCHEDULED'

    return {
        autoTopupStatus,
        autoTopupType,
        isAutoTopUpEnabled,
        isScheduledTopUpEnabled,
    }
}

/**
 * @returns true if ATU has been set up at some point in the past
 */
export function useHasAutoTopUpSettings() {
    const {autoTopUp, isLoading} = useAutoTopUpSettings()
    const hasAutoTopUpSettings =
        autoTopUp?.lowBalanceTopup?.threshold !== undefined ||
        autoTopUp.scheduledTopup?.frequency !== undefined

    return {
        hasAutoTopUpSettings,
        isLoading,
    }
}

export function useWalletDryThreshold() {
    const {showToast} = useToaster()
    const user = useLoggedInUser()
    const companyId = user?.companyId

    const {
        data: companyTopUpSettings,
        mutations: {updateTopUpSettings},
    } = useCompanyTopUpSettings()
    const {dryThreshold: walletDryThreshold} = companyTopUpSettings ?? {}

    async function updateWalletDryThreshold(dryThreshold: number | undefined) {
        if (!companyId) {
            return
        }

        try {
            await updateTopUpSettings({
                dryThreshold,
            })
        } catch (error) {
            reportError(error)
            showToast(t`Failed to update company settings`, {level: 'error'})
        }
    }

    return {
        walletDryThreshold: walletDryThreshold ?? null,
        mutations: {updateWalletDryThreshold},
    }
}

export interface WalletFeaturesResponse {
    features: WalletSettingsFeature[]
    openBankingTransactionLimit?: number
    autoTopupMinimumAmount?: number
}

export function useWalletFeatures() {
    const user = useLoggedInUser()
    const result = useSWRImmutable<WalletFeaturesResponse, Error>(
        user?.companyId
            ? `/wallet-management/rest/v4/companies/${user.companyId}/wallet-features`
            : null,
        getDeimos,
    )

    return {
        ...result,
        isLoading: !result.error && !result.data,
    }
}

export function useHasWalletFeature(featureName: WalletSettingsFeature) {
    const {data: walletSettings, isLoading} = useWalletFeatures()
    return {
        isLoading,
        hasWalletFeature: Boolean(walletSettings?.features?.includes(featureName)),
    }
}

export interface ActiveBankAccountDetails {
    id: string
    companyId: string
    currency: CurrencyType
    bankName?: string // human-readable string
    senderBank?: string // system-readable string
    bankAccountInfo: BankAccountInfo
}

type ActiveBankAccountsResponse = ActiveBankAccountDetails[]

function useActiveBankAccounts() {
    const user = useLoggedInUser()

    const result = useSWRImmutable<ActiveBankAccountsResponse, Error>(
        user?.companyId ? `/rest/v1/styx/companies/${user.companyId}/proof-of-funds/basic` : null,
        getDeimos,
    )
    return {
        ...result,
        isLoading: user?.companyId && !result.data && !result.error,
    }
}

export const useActiveBankAccountsWithSameCurrency = () => {
    const activeBankAccountsResult = useActiveBankAccounts()
    const {data: walletBalance} = useWalletBalance()
    const walletCurrency = walletBalance?.currency

    return {
        ...activeBankAccountsResult,
        data: filterBankAccountsByCurrency(activeBankAccountsResult.data, walletCurrency),
    }
}

const INSTANT_TOP_UP_BANK_BANK_LIMIT = 6
const INSTITUTIONS_ICON_SIZE = 80
export type OpenBankingBankDetails = {
    id: string
    institutionId: string
    name: string
    imageUrl: string
    status: 'APPROVED' | 'SUSPENDED' | 'DEGRADED_PERFORMANCE'
}

export type InstitutionsResponse = {result: OpenBankingBankDetails[]; totalItems: number}

type AutocompleteBankSelectionResult = {
    bankList: OpenBankingBankDetails[]
    totalItems: number
    isLoading: boolean
    hasError: boolean
}

const VRP_BANK_FEATURE_PAYMENT_TYPE = 'DOMESTIC_VARIABLE_RECURRING_PAYMENT'

export function useAutocompleteOpenBankingBankSelection(
    inputValue?: string,
    options?: {isAutoTopUpFlow?: boolean},
): AutocompleteBankSelectionResult {
    const {showToast} = useToaster()

    const user = useLoggedInUser()

    const [lastResult, setLastResult] = React.useState<AutocompleteBankSelectionResult>({
        bankList: [],
        totalItems: 0,
        isLoading: false,
        hasError: false,
    })
    const queryParams = queryString.stringify({
        name: inputValue || '',
        limit: INSTANT_TOP_UP_BANK_BANK_LIMIT,
        iconSize: INSTITUTIONS_ICON_SIZE,
        paymentType: options?.isAutoTopUpFlow ? VRP_BANK_FEATURE_PAYMENT_TYPE : undefined,
    })

    const isAbleToCall = Boolean(user.companyId)
    const response = useSWR<InstitutionsResponse>(
        isAbleToCall
            ? `/open-banking/rest/v1/companies/${user.companyId}/institutions?${queryParams}`
            : null,
        getDeimos,
    )

    React.useEffect(() => {
        if (!isAbleToCall || response.error) {
            showToast(t`An error occurred. Please try again later or contact support.`, {
                level: 'error',
            })
        }

        const isLoading = isAbleToCall && !response.error && !response.data
        if (isLoading) {
            setLastResult((prev) => ({
                ...prev,
                isLoading: true,
            }))
        } else if (!response.error) {
            setLastResult({
                bankList: response.data ? response.data.result : [],
                totalItems: response.data ? response.data.totalItems : 0,
                isLoading: false,
                hasError: false,
            })
        } else {
            setLastResult({
                bankList: [],
                totalItems: 0,
                isLoading: false,
                hasError: true,
            })
        }
    }, [response.error, response.data, isAbleToCall, showToast])

    return lastResult
}

export function filterBankAccountsByCurrency(
    bankAccounts?: ActiveBankAccountDetails[],
    selectedCurrency?: CurrencyType,
): ActiveBankAccountDetails[] | undefined {
    if (!bankAccounts) {
        return
    }

    // eslint-disable-next-line consistent-return
    return bankAccounts.filter(({currency}) => selectedCurrency === currency)
}

const DEFAULT_OPEN_BANKING_CALLBACK_URL =
    config.environment === 'production'
        ? `${window.location.origin}/wallet`
        : 'https://app.staging.pleo.io/wallet'

interface OpenBankingAuthorizationParams {
    companyId: string
    institutionId: string
    amountValue: number
    currency: CurrencyType
    iban?: string
    bankAccountDetails?: {sortCode: string; accountNumber: string}
}

interface OpenBankingAuthorizationBody {
    institutionId: string
    callBackUrl: string
    amount: {
        value: number
        currency: CurrencyType
    }
    accountIdentifications?: Array<{
        type: OpenBankingAccountIdentificationType
        identification: string
    }>
}

export async function getOpenBankingAuthorizationUrl(
    institutionAuthParams: OpenBankingAuthorizationParams,
    customCallBackUrl?: string,
): Promise<string> {
    const callBackUrl = customCallBackUrl ?? DEFAULT_OPEN_BANKING_CALLBACK_URL

    const body: OpenBankingAuthorizationBody = {
        institutionId: institutionAuthParams.institutionId,
        callBackUrl,
        amount: {
            value: institutionAuthParams.amountValue,
            currency: institutionAuthParams.currency,
        },
    }

    if (institutionAuthParams.iban) {
        body.accountIdentifications = [{type: 'IBAN', identification: institutionAuthParams.iban}]
    }

    return callApi(
        `/wallet-management/rest/v4/companies/${institutionAuthParams.companyId}/payments/authorize`,
        {
            auth: 'user',
            method: 'POST',
            body,
        },
    ).then((data) => data.institutionAuthorizationUrl)
}

export async function createOpenBankingPayment(companyId: string, consentToken: string) {
    return callApi(`/wallet-management/rest/v4/companies/${companyId}/payments`, {
        auth: 'user',
        method: 'POST',
        body: {consentToken},
    })
}

const DEFAULT_VRP_CALLBACK_URL = DEFAULT_OPEN_BANKING_CALLBACK_URL + '?paymentType=vrp'

type OpenBankingVrpAuthorizationBody = Omit<OpenBankingAuthorizationBody, 'callBackUrl'> & {
    callbackUrl: string
    companyId: string
}

export async function getOpenBankingVrpAuthorizationUrl(
    institutionAuthParams: OpenBankingAuthorizationParams,
    callBackUrl = DEFAULT_VRP_CALLBACK_URL,
) {
    const body: OpenBankingVrpAuthorizationBody = {
        institutionId: institutionAuthParams.institutionId,
        callbackUrl: callBackUrl,
        companyId: institutionAuthParams.companyId,
        amount: {
            currency: institutionAuthParams.currency,
            value: institutionAuthParams.amountValue,
        },
    }

    if (institutionAuthParams.bankAccountDetails) {
        body.accountIdentifications = [
            {
                type: 'SORT_CODE',
                identification: institutionAuthParams.bankAccountDetails.sortCode,
            },
            {
                type: 'ACCOUNT_NUMBER',
                identification: institutionAuthParams.bankAccountDetails.accountNumber,
            },
        ]
    }

    return callApi(`/open-banking/rest/v1/variable-recurring-payments`, {
        auth: 'user',
        method: 'POST',
        body,
    }).then((data) => data.institutionAuthorizationUrl)
}

export async function authorizeVrpAgreement(companyId: string, consentId: string) {
    const {id} = await getVrpAgreement(companyId)

    const queryParams = queryString.stringify(
        {
            companyId,
            consentId,
        },
        {addQueryPrefix: true},
    )

    return callApi(
        `/open-banking/rest/v1/variable-recurring-payments/${id}/authorize${queryParams}`,
        {
            auth: 'user',
            method: 'POST',
        },
    )
}

const getVrpAgreement = async (companyId: string) => {
    return callApi(
        `/open-banking/rest/v1/variable-recurring-payments/details?companyId=${companyId}`,
        {
            auth: 'user',
            method: 'GET',
        },
    )
}
